import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { MODULE_KEY } from '@/constants/modules';
import { PageContainer } from '@/components/layout/PageContainer';
import { AdminPageHeader } from '@/components/admin/shared/AdminPageHeader';
import { Button } from '@/components/ui/button';
import { FilePreviewModal } from '@/components/common/FilePreviewModal';
import { Icon } from '@/components/common/Icon';
import { showErrorNotification } from '@/utils/notifications';
import { useAuth } from '@/hooks/useAuth';
import { useHasWorkspaceModule } from '@/hooks/useHasWorkspaceModule';
import { useReportsSummaryQuery } from '@/hooks/useReportsQueries';
import { useDownloadReportPdf } from '@/hooks/useDownloadReportPdf';
import { useViewReportPdf } from '@/hooks/useViewReportPdf';
import { DateRangeControls } from './DateRangeControls';
import { KpiTiles } from './KpiTiles';
import { TreatmentsDeliveredChart } from './TreatmentsDeliveredChart';
import type { ReportsDateRangeValue } from './types';
import { toDateRangeParameters } from './types';
import { VisitsByStatusChart } from './VisitsByStatusChart';
import { VisitsByTypeChart } from './VisitsByTypeChart';

const EMPTY_DATE_RANGE: ReportsDateRangeValue = { dateFrom: '', dateTo: '' };

interface ReportPreview {
  url: string;
  filename: string;
}

export function ReportsPage() {
  const { t } = useTranslation('reports');
  const { activeWorkspace, isElevatedWorkspaceAccess } = useAuth();
  const hasReportsModule = useHasWorkspaceModule(MODULE_KEY.reports);
  const isOwner =
    (activeWorkspace !== null && activeWorkspace.isOwner === true) || isElevatedWorkspaceAccess;
  const [dateRange, setDateRange] = useState<ReportsDateRangeValue>(EMPTY_DATE_RANGE);
  const [preview, setPreview] = useState<ReportPreview | null>(null);

  const summaryQuery = useReportsSummaryQuery(toDateRangeParameters(dateRange));
  const downloadReportPdfMutation = useDownloadReportPdf();
  const viewReportPdfMutation = useViewReportPdf();

  useEffect(
    function revokePreviousPreviewUrl() {
      return function cleanup() {
        if (preview !== null) {
          URL.revokeObjectURL(preview.url);
        }
      };
    },
    [preview]
  );

  if (hasReportsModule === false || isOwner === false) {
    return <Navigate to={APPLICATION_ROUTES.workspaceDashboard} replace />;
  }

  return (
    <PageContainer width="full">
      <AdminPageHeader
        title={t('reports:pageTitle')}
        subtitle={t('reports:pageSubtitle')}
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              isLoading={viewReportPdfMutation.isPending}
              onClick={function viewReportPdf() {
                viewReportPdfMutation.mutate(toDateRangeParameters(dateRange), {
                  onSuccess: function openPreview(downloaded) {
                    setPreview({
                      url: URL.createObjectURL(downloaded.blob),
                      filename: downloaded.filename,
                    });
                  },
                  onError: function notifyViewFailure() {
                    showErrorNotification(t('reports:view.error'));
                  },
                });
              }}
            >
              {viewReportPdfMutation.isPending ? null : <Icon name="Eye" />}
              {t('reports:view.pdf')}
            </Button>
            <Button
              variant="outline"
              isLoading={downloadReportPdfMutation.isPending}
              onClick={function downloadReportPdf() {
                downloadReportPdfMutation.mutate(toDateRangeParameters(dateRange), {
                  onError: function notifyDownloadFailure() {
                    showErrorNotification(t('reports:download.error'));
                  },
                });
              }}
            >
              {downloadReportPdfMutation.isPending ? null : <Icon name="Download" />}
              {t('reports:download.pdf')}
            </Button>
          </div>
        }
      />

      <div className="mb-5">
        <DateRangeControls value={dateRange} onChange={setDateRange} />
      </div>

      <div className="space-y-5">
        <KpiTiles summary={summaryQuery.data} isLoading={summaryQuery.isLoading} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <VisitsByTypeChart dateRange={dateRange} />
          <VisitsByStatusChart dateRange={dateRange} />
        </div>

        <TreatmentsDeliveredChart dateRange={dateRange} />
      </div>

      <FilePreviewModal
        isOpen={preview !== null}
        onClose={function closePreview() {
          setPreview(null);
        }}
        url={preview === null ? '' : preview.url}
        filename={preview === null ? '' : preview.filename}
        contentType="application/pdf"
      />
    </PageContainer>
  );
}
